/**
 * Contact Form Service
 * Handles form submissions via EmailJS (simple email delivery)
 */

import emailjs from '@emailjs/browser';

// EmailJS configuration - replace with your actual EmailJS credentials
const EMAILJS_SERVICE_ID = 'service_frjhf5v';
const EMAILJS_TEMPLATE_ID = 'template_xfw95pa';
const EMAILJS_PUBLIC_KEY = 'f9kwaPX-7NUpPMR6_';

/**
 * Submit contact form data via EmailJS
 * @param {Object} formData - The form data to submit
 * @returns {Promise<Object>} - Response from the server
 */
export const submitContactForm = async (formData) => {
  try {
    // Validate form data
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      throw new Error('All fields are required');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Please enter a valid email address');
    }

    // Initialize EmailJS with your public key
    emailjs.init(EMAILJS_PUBLIC_KEY);

    // Prepare template parameters for EmailJS
    const templateParams = {
      from_name: formData.name.trim(),
      from_email: formData.email.trim().toLowerCase(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      investor_type: formData.investorType || 'Not specified',
      to_email: 'patrick@honestlee.app', // Your CTO's email
      reply_to: formData.email.trim().toLowerCase(),
      timestamp: new Date().toLocaleString(),
      user_agent: navigator.userAgent,
      page_url: window.location.href
    };

    // Send email using EmailJS
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    // Check if the email was sent successfully
    if (response.status === 200) {
      return {
        success: true,
        message: 'Message sent successfully! We\'ll get back to you soon.',
        timestamp: new Date().toISOString()
      };
    } else {
      throw new Error('Failed to send email');
    }

  } catch (error) {
    console.error('Error submitting contact form:', error);
    
    // Return a user-friendly error message
    let errorMessage = 'Failed to send message. Please try again.';
    
    if (error.message.includes('email')) {
      errorMessage = 'Please enter a valid email address.';
    } else if (error.message.includes('required')) {
      errorMessage = 'Please fill in all required fields.';
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      errorMessage = 'Network error. Please check your connection and try again.';
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Test the EmailJS connection
 * @returns {Promise<Object>} - Test result
 */
export const testConnection = async () => {
  try {
    // Initialize EmailJS
    emailjs.init(EMAILJS_PUBLIC_KEY);
    
    return {
      success: true,
      message: 'EmailJS connection test successful',
      service: 'EmailJS'
    };

  } catch (error) {
    console.error('EmailJS connection test failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get client IP address (for analytics)
 * @returns {Promise<string>} - Client IP address
 */
export const getClientIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP address:', error);
    return 'Unknown';
  }
};
